import React, { FC, useState } from 'react'

import { Checkbox } from '@style-port/roov-ui-components-react'
import { Icon, TableCell, Tooltip, Txt, LineLimitTxt } from 'src/components/atoms'
import { DateTxt, ProjectCountTxt } from 'src/components/molecules'
import { DeleteExternalUserModal } from 'src/components/organisms'

import { useAuthority, useToast } from 'src/hooks'
import { ExternalUserEntity, SelectableExternalUserEntity } from 'src/types'

type Props = {
  externalUser: SelectableExternalUserEntity
  onSelectUser: (user: SelectableExternalUserEntity) => void
  className?: string
}

export const ExternalUserListRow: FC<Props> = ({ externalUser, onSelectUser, className }) => {
  const [deleteExternalUser, setDeleteExternalUser] = useState<ExternalUserEntity | null>(null)
  const { canAssignProject } = useAuthority()

  const { displayStandardTextToast } = useToast()

  const handleClickDeleteExternalUser = (event: React.UIEvent<HTMLDivElement>) => {
    event.stopPropagation()
    setDeleteExternalUser(externalUser)
  }

  const handleOnSelect = (event: React.UIEvent<HTMLDivElement>) => {
    event.stopPropagation()
    onSelectUser(externalUser)
  }

  const afterDelete = async () => {
    displayStandardTextToast('ユーザーを削除しました')
  }

  return (
    <div className={className}>
      <div className="tw-flex tw-flex-nowrap tw-items-center tw-accent-standardText-secondary tw-h-[58px] tw-cursor-pointer tw-bg-surface-primary hover:tw-bg-surface-secondary">
        {canAssignProject && (
          <TableCell width={6}>
            <div role="presentation" onClick={handleOnSelect}>
              <Checkbox nonce={undefined} checked={externalUser.isSelected} padding="--s0" />
            </div>
          </TableCell>
        )}
        <TableCell width={6} align="center" className="tw-mr-s1">
          <Txt sizeType="s" className="tw-text-standardText-secondary">
            {externalUser.id}
          </Txt>
        </TableCell>
        <TableCell width={22}>
          <LineLimitTxt
            maxLineLimit={2}
            className="tw-text-standardText-primary tw-font-bold tw-mr-s-1"
          >
            {externalUser.name ?? '-'}
          </LineLimitTxt>
        </TableCell>
        <TableCell width={24}>
          <LineLimitTxt
            maxLineLimit={2}
            sizeType="s"
            className="tw-text-standardText-primary tw-mr-s-1"
          >
            {externalUser.email}
          </LineLimitTxt>
        </TableCell>
        <TableCell width={8} className="tw-mr-s1">
          <LineLimitTxt
            maxLineLimit={2}
            sizeType="s"
            className="tw-text-standardText-secondary tw-mr-s2"
          >
            {externalUser.keywords.length > 0 ? externalUser.keywords.join(', ') : '-'}
          </LineLimitTxt>
        </TableCell>
        <TableCell width={8} align="right" className="tw-mr-s1">
          <DateTxt
            date={externalUser.createdAt}
            sizeType="s"
            className="tw-text-standardText-secondary"
          />
        </TableCell>
        <TableCell align="right" className="tw-mr-s1">
          <ProjectCountTxt
            count={externalUser.projectCount}
            className="tw-text-standardText-secondary"
          />
        </TableCell>
        {canAssignProject && (
          <TableCell width={6} align="right" className="tw-mr-s-1">
            <Tooltip text="削除" placement="top" showIcon={false}>
              <div
                role="presentation"
                className="tw-p-r-3 tw-cursor-pointer"
                onClick={handleClickDeleteExternalUser}
              >
                <Icon
                  path="trash/soft/line"
                  size="--r1"
                  className="tw-text-standardText-secondary hover:tw-text-standardText-primary"
                />
              </div>
            </Tooltip>
          </TableCell>
        )}
      </div>

      {deleteExternalUser && (
        <DeleteExternalUserModal
          externalUsers={[externalUser]}
          isOpen={!!deleteExternalUser}
          onClose={() => setDeleteExternalUser(null)}
          afterDelete={afterDelete}
        />
      )}
    </div>
  )
}
